import { useState } from 'react'
import { Minus, Plus, TrendingDown, TrendingUp } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import type { AvailabilityMode, WorkerView } from '@/types/ui/resource-availability'
import {
  availableHeadcountAt,
  availableHeadcountInRange,
  endingInRangeCount,
  formatPercent,
  teamAveragePercentInRange,
} from '@/lib/utils/availability-utils'
import { formatDateRange } from '@/lib/utils/timeline-axis'
import { useLanguage } from '@/contexts/language-context'

interface AvailabilityKPICardsProps {
  workers: WorkerView[]
  mode: AvailabilityMode
  /** ISO yyyy-MM-dd. Heatmap's view-range start — KPIs aggregate across this range. */
  viewStart: string
  /** ISO yyyy-MM-dd. Heatmap's view-range end. */
  viewEnd: string
}

const DEFAULT_AVAILABILITY_THRESHOLD_PCT = 50
const THRESHOLD_STEP = 10

export function AvailabilityKPICards({ workers, mode, viewStart, viewEnd }: AvailabilityKPICardsProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.kpi

  const [thresholdPct, setThresholdPct] = useState(DEFAULT_AVAILABILITY_THRESHOLD_PCT)

  const teamAvg = teamAveragePercentInRange(workers, viewStart, viewEnd, mode)
  const endingCount = endingInRangeCount(workers, viewStart, viewEnd)
  const currentMonthIdx = workers[0]?.months.findIndex(m => m.isCurrentMonth) ?? -1
  const availableNow = currentMonthIdx >= 0
    ? availableHeadcountAt(workers, currentMonthIdx, thresholdPct)
    : 0
  const availableInRange = availableHeadcountInRange(workers, viewStart, viewEnd, thresholdPct)

  const avgLabel = mode === 'allocated'
    ? (t?.teamAvgAllocated || 'Team avg allocated')
    : (t?.teamAvgAvailable || 'Team avg available')
  const workerNoun = workers.length === 1
    ? (t?.workerSingular || 'worker')
    : (t?.workerPlural || 'workers')
  const rangeLabel = formatDateRange(viewStart, viewEnd)
  const trendText = availableInRange > availableNow
    ? (t?.upFromToday || 'Up from {n} today').replace('{n}', String(availableNow))
    : availableInRange < availableNow
      ? (t?.downFromToday || 'Down from {n} today').replace('{n}', String(availableNow))
      : (t?.sameAsToday || 'Same as today ({n})').replace('{n}', String(availableNow))

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{avgLabel}</p>
          <p className="text-2xl font-semibold">{formatPercent(teamAvg)}</p>
          <p className="text-xs text-muted-foreground mt-1">{workers.length} {workerNoun}</p>
          <p className="text-[10px] text-muted-foreground/80 mt-0.5 tabular-nums">{rangeLabel}</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            {t?.availableHeadcount || 'Available headcount*'}
          </p>
          <p className={`text-2xl font-semibold ${availableInRange > availableNow ? 'text-orange-600' : availableInRange < availableNow ? 'text-green-600' : ''}`}>
            {availableInRange}
          </p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            {availableInRange > availableNow ? (
              <TrendingUp className="w-3 h-3 text-orange-600" />
            ) : availableInRange < availableNow ? (
              <TrendingDown className="w-3 h-3 text-green-600" />
            ) : null}
            <span>{trendText}</span>
          </p>
          <p className="text-[10px] text-muted-foreground/80 mt-0.5 tabular-nums">{rangeLabel}</p>
          <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-dashed flex items-center flex-wrap gap-x-1">
            <span>{t?.thresholdPrefix || '* workers with ≥'}</span>
            <InlineStepper
              value={thresholdPct}
              onDecrement={() => setThresholdPct(v => Math.max(0, v - THRESHOLD_STEP))}
              onIncrement={() => setThresholdPct(v => Math.min(100, v + THRESHOLD_STEP))}
              decreaseAriaLabel={t?.decreaseThreshold || 'Decrease availability threshold'}
              increaseAriaLabel={t?.increaseThreshold || 'Increase availability threshold'}
            />
            <span>{t?.thresholdSuffix || '% availability at any point in range'}</span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            {t?.endingInRange || 'Engagements ending'}
          </p>
          <p className="text-2xl font-semibold text-orange-600">{endingCount}</p>
          <p className="text-[10px] text-muted-foreground/80 mt-0.5 tabular-nums">{rangeLabel}</p>
        </CardContent>
      </Card>
    </div>
  )
}

interface InlineStepperProps {
  value: number
  onDecrement: () => void
  onIncrement: () => void
  decreaseAriaLabel: string
  increaseAriaLabel: string
}

function InlineStepper({ value, onDecrement, onIncrement, decreaseAriaLabel, increaseAriaLabel }: InlineStepperProps) {
  return (
    <span className="inline-flex items-center align-middle normal-case tracking-normal rounded-md border border-input bg-background">
      <button
        type="button"
        onClick={onDecrement}
        aria-label={decreaseAriaLabel}
        className="rounded-l-md px-1 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-inset"
      >
        <Minus className="w-2.5 h-2.5" />
      </button>
      <span className="font-semibold text-foreground tabular-nums min-w-[1.25rem] text-center px-1 border-x border-input/60">
        {value}
      </span>
      <button
        type="button"
        onClick={onIncrement}
        aria-label={increaseAriaLabel}
        className="rounded-r-md px-1 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-inset"
      >
        <Plus className="w-2.5 h-2.5" />
      </button>
    </span>
  )
}
